import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, HeartHandshake, Loader2, ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CARE_TYPE_OPTIONS = [
  "Home Care",
  "Home Health",
  "Palliative Care",
  "Hospice Care",
  "Adult Day Care",
  "Home Physicians",
  "Senior Advisor",
  "Other",
];

const EMPTY = {
  organization_name: "",
  contact_name: "",
  email: "",
  phone: "",
  care_types: [],
  service_area: "",
  message: "",
};

export default function ProviderInquiryPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const toggleCareType = (label) => {
    setForm((prev) => ({
      ...prev,
      care_types: prev.care_types.includes(label)
        ? prev.care_types.filter((c) => c !== label)
        : [...prev.care_types, label],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["organization_name", "contact_name", "email", "phone"];
    const missing = required.filter((k) => !form[k]?.trim());
    if (missing.length) {
      toast.error("Please fill in every required field.");
      return;
    }
    if (form.care_types.length === 0) {
      toast.error("Please select at least one type of care you provide.");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/provider/inquiries`, {
        ...form,
        care_types: form.care_types.join(", "),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40 backdrop-blur-lg bg-opacity-95">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              data-testid="back-to-home"
              onClick={() => navigate("/")}
              aria-label="Back to home"
              className="text-text-secondary hover:text-primary transition-colors p-1"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="provider-inquiry-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center"
              aria-label="Explore care types"
            >
              <img src="/tyvena-lockup-horizontal.png" alt="Tyvena" className="h-9 md:h-10 w-auto" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-16">
        {submitted ? (
          <div
            data-testid="provider-inquiry-success"
            className="bg-white rounded-3xl border-2 border-primary/25 p-10 md:p-14 text-center shadow-sm"
          >
            <div className="inline-flex w-20 h-20 rounded-full bg-primary/10 items-center justify-center mb-6">
              <CheckCircle2 className="w-12 h-12 text-primary" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
              Thank you — we&apos;ve got it.
            </h1>
            <p className="font-body text-text-secondary text-lg leading-relaxed mb-8 max-w-xl mx-auto">
              A Tyvena team member will personally reach out within <strong>2 business days</strong> to talk through how we might work together and set up your listing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="return-home-btn"
                onClick={() => navigate("/")}
                className="bg-primary text-white hover:bg-[#238C82] rounded-full font-semibold px-8"
              >
                Return to home
              </Button>
              <Button
                data-testid="learn-more-btn"
                onClick={() => navigate("/tier-policy")}
                variant="outline"
                className="rounded-full font-semibold px-8"
              >
                Read our provider policy
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-body font-semibold mb-4">
                <HeartHandshake className="w-4 h-4" />
                For care providers
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary mb-4">
                Interested in joining Tyvena?
              </h1>
              <p className="font-body text-text-secondary text-lg leading-relaxed max-w-2xl mx-auto">
                Tell us a little about your organization and someone from our team will reach out to talk through the fit. No commitment, no pricing on the page — just a real conversation about the families you serve.
              </p>
            </div>

            {/* Patient-choice pledge — the tone providers should expect from us */}
            <div
              data-testid="provider-inquiry-pledge"
              className="rounded-2xl border border-primary/25 bg-primary/5 p-5 md:p-6 mb-8"
            >
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-heading font-bold text-primary text-base mb-1">
                    Families come first — that&apos;s our pledge to them, and to you.
                  </p>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">
                    Tyvena helps families discover the services, care and community resources they need for an aging loved one — right when they need them. Families choose who they want to learn more about and when they&apos;re ready to reach out, so you connect with people who are genuinely looking for help.
                  </p>
                </div>
              </div>
            </div>

            {/* Community-partner callout — routes qualifying orgs to the free listing conversation */}
            <div
              data-testid="nonprofit-callout"
              className="rounded-2xl border border-secondary/25 bg-secondary/5 p-5 md:p-6 mb-8 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <HeartHandshake className="w-8 h-8 text-secondary shrink-0" />
              <div className="flex-1">
                <p className="font-heading font-bold text-secondary text-base mb-1">
                  Are you a community partner?
                </p>
                <p className="font-body text-sm text-text-secondary leading-relaxed">
                  If your organization offers free services, resources, or support for families, caregivers, or patients — we&apos;d love to talk with you to see if a free listing on Tyvena would be of value to our users. Use the same form below and choose &ldquo;Other&rdquo; for the type of care.
                </p>
              </div>
            </div>

            {/* Form */}
            <form
              data-testid="provider-inquiry-form"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 shadow-sm space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Organization / Agency name *
                  </Label>
                  <Input
                    data-testid="pi-org-name"
                    value={form.organization_name}
                    onChange={handleChange("organization_name")}
                    placeholder="e.g., Cardinal Home Health"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Contact person *
                  </Label>
                  <Input
                    data-testid="pi-contact-name"
                    value={form.contact_name}
                    onChange={handleChange("contact_name")}
                    placeholder="Your full name"
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Email *
                  </Label>
                  <Input
                    data-testid="pi-email"
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="you@youragency.com"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Phone *
                  </Label>
                  <Input
                    data-testid="pi-phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder="(555) 555-5555"
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label className="font-body font-semibold text-text-primary block mb-2">
                  Type(s) of care you provide *
                </Label>
                <div className="flex flex-wrap gap-2" data-testid="pi-care-types">
                  {CARE_TYPE_OPTIONS.map((label) => {
                    const active = form.care_types.includes(label);
                    return (
                      <button
                        type="button"
                        key={label}
                        data-testid={`pi-care-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => toggleCareType(label)}
                        className={`px-4 py-2 rounded-full text-sm font-body font-medium transition-all border ${
                          active
                            ? "bg-primary text-white border-primary shadow-sm"
                            : "bg-white text-text-secondary border-[#E2E8F0] hover:border-primary/40"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="font-body font-semibold text-text-primary">
                  Service area <span className="font-normal text-text-secondary">(optional)</span>
                </Label>
                <Input
                  data-testid="pi-service-area"
                  value={form.service_area}
                  onChange={handleChange("service_area")}
                  placeholder="Zip codes or cities you serve — e.g., Charlotte NC, 28202"
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="font-body font-semibold text-text-primary">
                  Anything else you&apos;d like us to know? <span className="font-normal text-text-secondary">(optional)</span>
                </Label>
                <Textarea
                  data-testid="pi-message"
                  value={form.message}
                  onChange={handleChange("message")}
                  rows={4}
                  placeholder="Tell us about your team, what makes you unique, or the families you'd love to reach."
                  className="mt-2 font-body"
                />
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-3 border-t border-[#E2E8F0]">
                <p className="font-body text-xs text-text-secondary">
                  By submitting, you agree we can email or call the contact info above.
                </p>
                <Button
                  data-testid="pi-submit"
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary text-white hover:bg-[#238C82] rounded-full font-semibold px-8"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    "Request info"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
